import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../../shared/abstractStore';
import { editSubscriptionViewModelQuery, editSubscriptionViewModelQueryVariables } from '../../generated/editSubscriptionViewModelQuery';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { blobReferenceFieldsFragment } from '../../models/BlobReference';
import { Guid } from 'guid-string';
import { subscriptionFieldsFragment } from '../../models/Subscription';

/**
 * View model for EditSubscription that replaces the call to useSubscription() and loads the right related data.
 * @param options
 */
export function useEditSubscriptionViewModel(id: string | undefined | null, options: AsyncLoadOptions = {}): AsyncLoadResult<editSubscriptionViewModelQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<editSubscriptionViewModelQuery, editSubscriptionViewModelQueryVariables>(
        gql`
        query editSubscriptionViewModelQuery ($id: ID!) {
            model: subscription(id: $id) {
                ...subscriptionFields

                brandImageBlobReference {
                    ...blobReferenceFields
                }
            }
        }

        ${subscriptionFieldsFragment}
        ${blobReferenceFieldsFragment}

        `,
        {
            variables: {
                id: id ?? Guid.empty
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
